import { useRef, useEffect, useState } from "react";
import React from "react";
import { motion } from "framer-motion";
import useScrollVelocitySkew from "../../hooks/useScrollVelocitySkew";

// ── Timing constants ──────────────────────────────────────────────────────
const STAGGER       = 20;   // ms between each character
const LINE_GAP      = 4;    // extra character-widths of pause between line 1 → line 2
const DURATION      = 750;  // ms per character animation
const EASE          = "cubic-bezier(0.22, 1, 0.36, 1)";
const INITIAL_DELAY = 180;  // ms: lets parent reveal/fade start first

// Map heading tag names → Framer Motion equivalents
const MOTION_TAG = {
  h1: motion.h1, h2: motion.h2, h3: motion.h3,
  h4: motion.h4, h5: motion.h5, h6: motion.h6,
  p:  motion.p,  div: motion.div,
};

// Line-level clip: overflow:hidden masks characters entering from below.
// paddingBottom leaves room for descenders (g, p, y, j in Cormorant).
const lineWrap = {
  display:      "block",
  overflow:     "hidden",
  paddingBottom: "0.14em",
  marginBottom:  "-0.14em",
};

function charStyle(visible, delayMs) {
  return {
    display:    "inline-block",
    whiteSpace: "pre",
    transform:  visible ? "translateY(0)" : "translateY(110%)",
    transition: `transform ${DURATION}ms ${EASE} ${INITIAL_DELAY + delayMs}ms`,
    willChange: "transform",
  };
}

// Split a plain string into per-character animated spans.
function splitString(text, startDelay, visible) {
  return text.split("").map((ch, i) => (
    <span key={i} style={charStyle(visible, startDelay + i * STAGGER)}>
      {ch === " " ? " " : ch}
    </span>
  ));
}

// Render a line — handles plain string or a wrapping React element
// (e.g. <em className="italic text-amber">word</em>).
function renderLine(content, startDelay, visible) {
  if (typeof content === "string") {
    return (
      <span style={lineWrap}>
        {splitString(content, startDelay, visible)}
      </span>
    );
  }

  if (React.isValidElement(content)) {
    const text = content.props.children;

    if (typeof text === "string") {
      const chars = text.split("").map((ch, i) => (
        <span key={i} style={charStyle(visible, startDelay + i * STAGGER)}>
          {ch === " " ? " " : ch}
        </span>
      ));
      return (
        <span style={lineWrap}>
          {React.cloneElement(content, {}, ...chars)}
        </span>
      );
    }
  }

  // Fallback — animate whole block as before
  return (
    <span style={lineWrap}>
      <span style={charStyle(visible, startDelay)}>{content}</span>
    </span>
  );
}

function textLength(content) {
  if (typeof content === "string") return content.length;
  if (React.isValidElement(content) && typeof content.props.children === "string") {
    return content.props.children.length;
  }
  return 0;
}

export default function SplitHeading({
  line1,
  line2,
  as: Tag = "h2",
  className = "",
  style = {},
  stagger: _ignored, // kept for API compat, ignored
  ...rest
}) {
  const ref     = useRef(null);
  const [visible, setVisible] = useState(false);
  const skewX   = useScrollVelocitySkew(3);   // ← velocity-driven lean
  const MotionTag = MOTION_TAG[Tag] ?? motion.h2;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); io.disconnect(); }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const line2Delay = (textLength(line1) + LINE_GAP) * STAGGER;

  return (
    <MotionTag
      ref={ref}
      className={className}
      style={{ ...style, skewX }}
      {...rest}
    >
      {renderLine(line1, 0, visible)}
      {line2 && renderLine(line2, line2Delay, visible)}
    </MotionTag>
  );
}
